import { parseXrkBuffer, sessionToJson } from '@kangaroo/mychron/xrk';

import { fetchDataFileContent } from '../dataFiles';
import { formatLapTime } from '../../lib/lapFormat';
import { withGpsDerivedTelemetry } from '../../lib/gpsDerivedTelemetry';
import {
  formatTraceClock,
  isMyChronSessionExport,
  type MyChronSessionExport,
} from '../../lib/telemetrySession';
import type { CircuitMapOverlay } from '../../lib/circuitMapOverlay';
import { resolveSessionRawLaps, type ResliceLapsResult } from '../../lib/circuitLapReslice';
import { isDemoSessionId } from './sessionList';
import { fetchSessionArtifactExport, type SessionArtifactView } from './sessionArtifacts';

export type SessionLapKind = 'flying' | 'out' | 'in' | 'crash';

export type SessionLapOption = {
  lapNumber: number;
  durationMs: number;
  label: string;
  gapSec: number | null;
  isBest: boolean;
  kind: SessionLapKind;
  rowLabel: string;
  selectable: boolean;
};

export async function fetchSessionExport(
  sessionId: string,
  options: {
    view?: SessionArtifactView;
    requestScope?: string;
    onPreview?: (session: MyChronSessionExport) => void;
  } = {},
): Promise<MyChronSessionExport | null> {
  if (isDemoSessionId(sessionId)) {
    const response = await fetch('/demo/a_0180.xrz');
    if (!response.ok) {
      throw new Error(`Unable to load demo session (${response.status})`);
    }

    const payload = sessionToJson(parseXrkBuffer(Buffer.from(await response.arrayBuffer())));
    return isMyChronSessionExport(payload) ? withGpsDerivedTelemetry(payload) : null;
  }

  const artifactSession = await fetchSessionArtifactExport(sessionId, options.view ?? 'map', {
    ...options,
    onPreview: options.onPreview
      ? (preview) => options.onPreview?.(withGpsDerivedTelemetry(preview))
      : undefined,
  });
  if (artifactSession) return withGpsDerivedTelemetry(artifactSession);

  const payload = await fetchDataFileContent(sessionId, 'overview');
  return isMyChronSessionExport(payload) ? withGpsDerivedTelemetry(payload) : null;
}

export function buildSessionLapOptions(
  session: MyChronSessionExport | null | undefined,
  circuitOverlay?: CircuitMapOverlay | null,
): SessionLapOption[] {
  if (!session) {
    return [];
  }

  const laps = resolveSessionRawLaps(session, circuitOverlay);
  if (laps.length === 0) {
    return [];
  }

  const bestDurationMs = [...laps].sort((left, right) => left.durationMs - right.durationMs)[0]?.durationMs ?? null;
  const bestLapNumber = bestDurationMs === null
    ? null
    : laps.find((lap) => lap.durationMs === bestDurationMs)?.lapNumber ?? null;

  return laps
    .slice()
    .sort((left, right) => left.lapNumber - right.lapNumber)
    .map((lap) => ({
      lapNumber: lap.lapNumber,
      durationMs: lap.durationMs,
      label: formatLapTime(lap.durationMs) ?? `L${lap.lapNumber}`,
      gapSec: bestDurationMs === null || lap.durationMs === bestDurationMs
        ? null
        : (lap.durationMs - bestDurationMs) / 1000,
      isBest: lap.lapNumber === bestLapNumber,
      kind: 'flying' as const,
      rowLabel: `L${lap.lapNumber}`,
      selectable: true,
    }));
}

export function buildCircuitSessionLapOptions(reslice: ResliceLapsResult | null | undefined): SessionLapOption[] {
  if (!reslice?.laps.length) {
    return [];
  }

  const flying = reslice.laps.filter((lap) => lap.kind === 'flying');
  const bestFlying = flying.reduce<(typeof flying)[number] | null>(
    (best, lap) => (!best || lap.durationSec < best.durationSec ? lap : best),
    null,
  );

  return reslice.laps.map((lap) => {
    const durationMs = Math.round(lap.durationSec * 1000);
    const rowLabel =
      lap.kind === 'out'
        ? 'Out'
        : lap.kind === 'in'
          ? 'In'
          : lap.kind === 'crash'
            ? 'Crash'
            : `L${lap.lapNumber}`;

    return {
      lapNumber: lap.lapNumber,
      durationMs,
      label: formatTraceClock(lap.durationSec),
      gapSec:
        lap.kind === 'flying' && bestFlying && lap.lapNumber !== bestFlying.lapNumber
          ? lap.durationSec - bestFlying.durationSec
          : null,
      isBest: Boolean(bestFlying && lap.lapNumber === bestFlying.lapNumber && lap.kind === 'flying'),
      kind: lap.kind,
      rowLabel,
      selectable: lap.kind === 'flying',
    };
  });
}

export function pickRankedLapBySpeedIndex(
  laps: SessionLapOption[],
  rankIndex: number,
) {
  const ranked = laps
    .filter((lap) => lap.selectable)
    .sort((left, right) => left.durationMs - right.durationMs);

  if (ranked.length === 0) {
    return null;
  }

  return ranked[Math.min(rankIndex, ranked.length - 1)]?.lapNumber ?? null;
}

export function pickDefaultLapNumber(
  session: MyChronSessionExport | null | undefined,
  circuitOverlay?: CircuitMapOverlay | null,
) {
  if (!session) {
    return null;
  }

  return pickRankedLapBySpeedIndex(buildSessionLapOptions(session, circuitOverlay), 0);
}

export function pickDefaultCompareLapNumber(
  session: MyChronSessionExport | null | undefined,
  slotIndex: number,
  circuitOverlay?: CircuitMapOverlay | null,
) {
  if (!session) {
    return null;
  }

  return pickRankedLapBySpeedIndex(buildSessionLapOptions(session, circuitOverlay), slotIndex + 1);
}

export function pickDefaultCompareLapFromOptions(
  laps: SessionLapOption[],
  slotIndex: number,
) {
  return pickRankedLapBySpeedIndex(laps, slotIndex + 1);
}

export function sessionExportCacheKey(sessionId: string | null | undefined, view?: SessionArtifactView) {
  return sessionId ? (['sessions', 'export', sessionId, ...(view ? [view] : [])] as const) : null;
}
